import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges,} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {DefaultValues} from '@maporium-workspace/shared';
import {OverlayElement} from '../../api/interfaces/overlay-element.interface';
import {SidebarFormAbstractComponent} from '../abstract/sidebar-form-abstract.component';
import {
  TitleDescriptionInputComponent
} from '../../shared/forms/title-description-input/title-description-input.component';
import {CdkAccordion} from '@angular/cdk/accordion';
import {
  MaporiumAccordionItemComponent
} from '../../shared/components/maporium-accordion/maporium-accordion-item.component';
import {OverlayElementStyleLightComponent} from './sections/overlay-element-style-light.component';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'maporium-overlay-element-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TitleDescriptionInputComponent,
    CdkAccordion,
    MaporiumAccordionItemComponent,
    OverlayElementStyleLightComponent,
    TranslatePipe,
  ],
  templateUrl: './overlay-element-form.component.html',
  styleUrls: ['./overlay-element-form.component.scss'],
})
export class OverlayElementFormComponent
  extends SidebarFormAbstractComponent<OverlayElement>
  implements OnInit, OnChanges, OnDestroy {
  @Input() element: OverlayElement | null = null;
  @Output() updateEvent = new EventEmitter<OverlayElement>();
  @Output() deleteEvent = new EventEmitter<string>();

  formGroup = new FormGroup({
    name: new FormControl(''),
    description: new FormControl(''),
    properties: new FormGroup({
      backgroundColor: new FormControl(DefaultValues.overlayElement.properties.backgroundColor),
      borderColor: new FormControl(DefaultValues.overlayElement.properties.borderColor),
      borderWidth: new FormControl(DefaultValues.overlayElement.properties.borderWidth),
      borderStyle: new FormControl(DefaultValues.overlayElement.properties.borderStyle),
      opacity: new FormControl(DefaultValues.overlayElement.properties.opacity),
    }),
    excludedFromState: new FormControl('false'),
  });

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    if (this.element?.id) {
      this.update();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['element'] && changes['element'].currentValue) {
      const element = changes['element'].currentValue as OverlayElement;
      const idChanged = changes['element'].previousValue?.id !== element.id;

      if (idChanged || changes['element'].firstChange) {
        this.patchForm(element);
      }
    }
  }

  patchForm(element: OverlayElement): void {
    this.isPatchingForm = true;
    this.formGroup.patchValue(
      {
        name: element.name || '',
        description: element.description || '',
        properties: {
          backgroundColor: element.properties?.backgroundColor || DefaultValues.overlayElement.properties.backgroundColor,
          borderColor: element.properties?.borderColor || DefaultValues.overlayElement.properties.borderColor,
          borderWidth: element.properties?.borderWidth ?? DefaultValues.overlayElement.properties.borderWidth,
          borderStyle: element.properties?.borderStyle || DefaultValues.overlayElement.properties.borderStyle,
          opacity: element.properties?.opacity ?? DefaultValues.overlayElement.properties.opacity,
        },
        excludedFromState: element.excludedFromState === true ? 'true' : 'false',
      },
      {emitEvent: false},
    );
    setTimeout(() => (this.isPatchingForm = false), this.debounceTime + 100);
  }

  update(): void {
    if (!this.element) return;
    const raw = this.formGroup.getRawValue();
    const updated: OverlayElement = {
      ...this.element,
      name: raw.name as string,
      description: raw.description as string,
      properties: {
        ...this.element.properties,
        backgroundColor: raw.properties.backgroundColor as string,
        borderColor: raw.properties.borderColor as string,
        borderWidth: raw.properties.borderWidth as number,
        borderStyle: raw.properties.borderStyle as 'solid' | 'dashed' | 'dotted',
        opacity: raw.properties.opacity as number,
      },
      excludedFromState: raw.excludedFromState === 'true',
    };
    this.updateEvent.emit(updated);
  }

  onNameDescriptionChange(event: { title: string; description?: string }): void {
    this.formGroup.patchValue({
      name: event.title,
      description: event.description,
    });
  }

  selectNameInput(): void {
    // Not applicable for overlay elements
  }

  resetState(): void {
    // Not applicable for overlay elements
  }
}
